import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Alert,
  Grid,
  Card,
  CardContent,
  Button,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { MentionSearchService } from '../../services/mentionSearchService';

/**
 * DatabaseTasks Page
 *
 * Admin page for database maintenance operations and migrations
 */
export const DatabaseTasks: React.FC = () => {
  const navigate = useNavigate();
  const [migrationRunning, setMigrationRunning] = useState(false);
  const [migrationCompleted, setMigrationCompleted] = useState(false);
  const [migrationResult, setMigrationResult] = useState<{ success: number; failed: number } | null>(null);
  const [migrationError, setMigrationError] = useState<string | null>(null);

  const handleRebuildSearchIndex = async (): Promise<void> => {
    if (!window.confirm(
      'Rebuild User Search Index?\n\n' +
      'This will update search tokens for all users to enable full email search.\n' +
      'The operation is idempotent and safe to run multiple times.\n\n' +
      'Continue?'
    )) {
      return;
    }

    setMigrationRunning(true);
    setMigrationCompleted(false);
    setMigrationError(null);
    setMigrationResult(null);

    try {
      console.log('🔄 Starting user search index rebuild migration...');
      const result = await MentionSearchService.rebuildSearchIndex();

      setMigrationResult(result);
      setMigrationCompleted(true);

      console.log('✅ Migration complete!');
      console.log(`   Success: ${result.success} users`);
      console.log(`   Failed: ${result.failed} users`);
    } catch (err: any) {
      console.error('❌ Migration failed:', err);
      setMigrationError(err.message || 'Unknown error occurred');
    } finally {
      setMigrationRunning(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Back Button */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/admin')}
        sx={{ mb: 3 }}
      >
        Back to Dashboard
      </Button>

      <Typography variant="h4" gutterBottom>
        Database Tasks
      </Typography>

      <Alert severity="warning" sx={{ mb: 4 }}>
        <Typography variant="body2">
          <strong>Admin Only:</strong> These operations modify database records.
          Only run tasks if you understand what they do.
        </Typography>
      </Alert>

      <Grid container spacing={3}>
        {/* User Search Index Rebuild */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6">
                  Rebuild User Search Index
                </Typography>
                <Chip
                  label="Idempotent"
                  color="success"
                  size="small"
                  icon={<CheckCircleIcon />}
                />
              </Box>

              <Typography variant="body2" color="text.secondary" paragraph>
                Updates search tokens for all users to enable full email search functionality.
                Safe to run multiple times.
              </Typography>

              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" component="div">
                  <strong>What this does:</strong>
                  <List dense>
                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <InfoIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Fetches all users from 'users' collection" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <InfoIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Generates enhanced search tokens (full emails + progressive substrings)" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <InfoIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Updates 'userSearchIndex' collection" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <InfoIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Includes linked person names from genealogy" />
                    </ListItem>
                  </List>
                </Typography>
              </Alert>

              {migrationRunning && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    Rebuilding search index...
                  </Typography>
                  <LinearProgress />
                </Box>
              )}

              {migrationCompleted && migrationResult && (
                <Alert
                  severity={migrationResult.failed === 0 ? 'success' : 'warning'}
                  sx={{ mb: 2 }}
                  icon={migrationResult.failed === 0 ? <CheckCircleIcon /> : <WarningIcon />}
                >
                  <Typography variant="body2">
                    <strong>Migration completed!</strong>
                    <br />
                    Successfully indexed: {migrationResult.success} users
                    <br />
                    Failed: {migrationResult.failed} users
                  </Typography>
                </Alert>
              )}

              {migrationError && (
                <Alert severity="error" sx={{ mb: 2 }} icon={<ErrorIcon />}>
                  <Typography variant="body2">
                    <strong>Migration failed:</strong> {migrationError}
                  </Typography>
                </Alert>
              )}

              <Button
                variant="contained"
                color="primary"
                startIcon={<PlayArrowIcon />}
                onClick={handleRebuildSearchIndex}
                disabled={migrationRunning}
                fullWidth
              >
                {migrationRunning ? 'Running Migration...' : 'Run Migration'}
              </Button>

              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                Check browser console for detailed progress logs
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Future database tasks can be added here */}
        {/* Example:
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Another Database Task
              </Typography>
              ...
            </CardContent>
          </Card>
        </Grid>
        */}
      </Grid>

      <Box sx={{ mt: 4 }}>
        <Typography variant="caption" color="text.secondary">
          All operations are logged to the browser console
        </Typography>
      </Box>
    </Container>
  );
};

export default DatabaseTasks;
